import React, { useState } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Divider from "@mui/material/Divider";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import RocketLaunchRoundedIcon from "@mui/icons-material/RocketLaunchRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";

import {
  MetricProfileInput,
  RegisterWbrInput,
  RegisterWbrResult,
  registerWbr,
} from "../api/registerWbr";

interface OnboardWbrProps {
  onRegistered: (result: RegisterWbrResult) => void;
}

const SectionTitle: React.FC<{
  step: number;
  title: string;
  subtitle: string;
}> = ({ step, title, subtitle }) => (
  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
    <Box
      sx={{
        width: 28,
        height: 28,
        borderRadius: "50%",
        bgcolor: "primary.main",
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 700,
        fontSize: 14,
      }}
    >
      {step}
    </Box>
    <Box>
      <Typography variant="subtitle1">{title}</Typography>
      <Typography variant="body2" color="text.secondary">
        {subtitle}
      </Typography>
    </Box>
  </Stack>
);

const toList = (s: string): string[] =>
  s
    .split("\n")
    .map((x) => x.trim())
    .filter((x) => x.length > 0);

const EXAMPLE_PROFILES_JSON = `[
  {
    "name": "core",
    "extract_queries": ["SELECT ... FROM ..."],
    "metrics": ["revenue", "acos"],
    "report_sections": ["summary", "key_metrics", "narrative"]
  }
]`;

/**
 * Parse the metric-profiles JSON. Tolerant of several shapes:
 *  - a top-level array of profile objects
 *  - an object with a `metric_profiles` (or `metricProfiles` / `profiles`) array
 *  - a single profile object
 *  - a full WBR config document (top-level `metrics` definitions + optional
 *    `data_sources` / `render`), from which a single profile is derived.
 * Also accepts singular/alternate field names for the inner lists. Returns the
 * parsed profiles or throws with a human-readable message.
 */
function parseProfiles(raw: string): MetricProfileInput[] {
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch (e) {
    throw new Error(
      "Invalid JSON: " + (e instanceof Error ? e.message : String(e))
    );
  }

  const asList = (v: unknown): string[] => {
    if (Array.isArray(v)) return v.map((x) => String(x));
    if (typeof v === "string" && v.trim()) return [v.trim()];
    return [];
  };

  const looksLikeProfile = (o: Record<string, unknown>): boolean =>
    "name" in o ||
    "extract_queries" in o ||
    "extractQueries" in o ||
    "report_sections" in o ||
    "reportSections" in o;

  // A full WBR config: top-level `metrics` is an array of metric *definitions*
  // (objects with id/display/formula), plus data_sources / dimensions / render.
  const isWbrConfig = (o: Record<string, unknown>): boolean => {
    const hasMetricDefs =
      Array.isArray(o.metrics) &&
      (o.metrics as unknown[]).some(
        (m) =>
          m &&
          typeof m === "object" &&
          ("id" in (m as object) || "display" in (m as object))
      );
    return (
      hasMetricDefs ||
      "data_sources" in o ||
      "dataSources" in o ||
      "render" in o
    );
  };

  // Derive a single MetricProfile from a full WBR config so it satisfies
  // validation. The raw document is the source of truth (uploaded to S3);
  // these derived fields just describe it.
  const deriveFromConfig = (o: Record<string, unknown>): MetricProfileInput => {
    const name = String(
      o.wbr_name ?? o.wbrName ?? o.name ?? o.team ?? "default"
    ).trim();

    // extract_queries: derive from the data source tables, else a placeholder.
    const ds = (o.data_sources ?? o.dataSources ?? {}) as Record<
      string,
      unknown
    >;
    const tables: string[] = [];
    Object.values(ds).forEach((src) => {
      if (
        src &&
        typeof src === "object" &&
        Array.isArray((src as any).tables)
      ) {
        (src as any).tables.forEach((t: unknown) => tables.push(String(t)));
      }
    });
    const extract_queries =
      tables.length > 0
        ? tables.map((t) => `SELECT * FROM ${t}`)
        : ["-- defined in uploaded WBR config"];

    // metrics: the metric ids (or displays).
    const metricDefs = Array.isArray(o.metrics) ? (o.metrics as any[]) : [];
    const metrics = metricDefs
      .map((m) => String(m?.id ?? m?.display ?? "").trim())
      .filter((x) => x.length > 0);

    // report_sections: from render.output_columns, else a default.
    const render = (o.render ?? {}) as Record<string, unknown>;
    const cols = Array.isArray(render.output_columns)
      ? (render.output_columns as any[])
      : Array.isArray(render.outputColumns)
      ? (render.outputColumns as any[])
      : [];
    const report_sections = cols
      .map((c) => String(c?.name ?? c?.label ?? c?.type ?? "").trim())
      .filter((x) => x.length > 0);

    return {
      name: name || "default",
      extract_queries,
      metrics,
      report_sections:
        report_sections.length > 0 ? report_sections : ["summary"],
    };
  };

  let arr: unknown;
  if (Array.isArray(data)) {
    arr = data;
  } else if (data && typeof data === "object") {
    const obj = data as Record<string, unknown>;
    if (isWbrConfig(obj)) {
      return [deriveFromConfig(obj)];
    }
    arr =
      obj.metric_profiles ??
      obj.metricProfiles ??
      obj.profiles ??
      // a single profile object → wrap it
      (looksLikeProfile(obj) ? [obj] : undefined);
  }

  if (!Array.isArray(arr)) {
    throw new Error(
      "Expected a JSON array of metric profiles, an object with a 'metric_profiles' array, a single profile object, or a WBR config document."
    );
  }

  return arr.map((p, i) => {
    if (!p || typeof p !== "object")
      throw new Error(`Profile ${i + 1} is not an object`);
    const o = p as Record<string, unknown>;
    return {
      name: String(o.name ?? "").trim(),
      extract_queries: asList(o.extract_queries ?? o.extractQueries),
      metrics: asList(o.metrics),
      report_sections: asList(o.report_sections ?? o.reportSections),
    };
  });
}

const OnboardWbr: React.FC<OnboardWbrProps> = ({ onRegistered }) => {
  const [name, setName] = useState("");
  const [schedule, setSchedule] = useState("0 6 * * MON");
  const [recipients, setRecipients] = useState("");
  const [profilesJson, setProfilesJson] = useState("");
  const [profilesFileName, setProfilesFileName] = useState<string | null>(null);
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [skills, setSkills] = useState<{ name: string; body: string }[]>([]);
  const [skillError, setSkillError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<RegisterWbrResult | null>(null);

  const onSkillFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length === 0) return;
    setSkillError(null);
    const added: { name: string; body: string }[] = [];
    for (const file of files) {
      const body = await file.text();
      if (!body.trim()) {
        setSkillError(`Skill file '${file.name}' is empty`);
        continue;
      }
      // strip the .md extension for the skill name
      const name = file.name.replace(/\.md$/i, "");
      added.push({ name, body });
    }
    setSkills((prev) => {
      // de-dupe by name: a re-uploaded skill replaces the prior one
      const byName = new Map(prev.map((s) => [s.name, s]));
      added.forEach((s) => byName.set(s.name, s));
      return Array.from(byName.values());
    });
    e.target.value = "";
  };

  const removeSkill = (name: string) =>
    setSkills((prev) => prev.filter((s) => s.name !== name));

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    setProfilesJson(text);
    setProfilesFileName(file.name);
    // validate immediately for quick feedback
    try {
      parseProfiles(text);
      setJsonError(null);
    } catch (err) {
      setJsonError(String(err instanceof Error ? err.message : err));
    }
    // allow re-uploading the same file
    e.target.value = "";
  };

  const onJsonChange = (v: string) => {
    setProfilesJson(v);
    setProfilesFileName(null);
    if (!v.trim()) {
      setJsonError(null);
      return;
    }
    try {
      parseProfiles(v);
      setJsonError(null);
    } catch (err) {
      setJsonError(String(err instanceof Error ? err.message : err));
    }
  };

  const parsedCount = (() => {
    if (!profilesJson.trim()) return 0;
    try {
      return parseProfiles(profilesJson).length;
    } catch {
      return 0;
    }
  })();

  const buildInput = (): RegisterWbrInput => {
    // wbr_id / team_id are generated by the backend for now.
    const metric_profiles = parseProfiles(profilesJson);
    return {
      name: name.trim(),
      metric_profiles,
      prompt_templates: [],
      skills,
      schedule: schedule.trim(),
      recipients: toList(recipients),
    };
  };

  const submit = async () => {
    setSubmitting(true);
    setResult(null);
    try {
      const input = buildInput();
      const res = await registerWbr(input);
      setResult(res);
      if (res.ok) onRegistered(res);
    } catch (e) {
      setResult({ ok: false, errors: [String(e)], message: "Request failed" });
    } finally {
      setSubmitting(false);
    }
  };

  const recipientCount = toList(recipients).length;
  const canSubmit =
    !submitting && parsedCount > 0 && !jsonError && recipientCount >= 1;

  return (
    <Box sx={{ maxWidth: 920, mx: "auto" }}>
      <Typography variant="h5" sx={{ mb: 0.5 }}>
        Onboard a new WBR
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Register a Weekly Business Review. The WBR and team identifiers are
        generated automatically. On registration, AutoWBR validates the
        definition, stores it in PostgreSQL, and generates the WBR's agent (its
        Airflow DAG).
      </Typography>

      {result && (
        <Alert severity={result.ok ? "success" : "error"} sx={{ mb: 2.5 }}>
          {result.message}
          {!result.ok && result.errors && result.errors.length > 0 && (
            <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
              {result.errors.map((e, i) => (
                <li key={i}>{e}</li>
              ))}
            </ul>
          )}
          {result.ok && result.wbr_id && (
            <Box sx={{ mt: 0.5 }}>
              Assigned WBR id <strong>{result.wbr_id}</strong>
              {result.team_id && (
                <>
                  {" "}
                  · team <strong>{result.team_id}</strong>
                </>
              )}
            </Box>
          )}
        </Alert>
      )}

      <Stack spacing={2.5}>
        {/* Identity */}
        <Card>
          <CardContent>
            <SectionTitle
              step={1}
              title="Identity"
              subtitle="What is this WBR called? Ids are assigned by AutoWBR."
            />
            <TextField
              fullWidth
              label="Display name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="OS Weekly Business Review"
            />
          </CardContent>
        </Card>

        {/* Metric profiles via JSON */}
        <Card>
          <CardContent>
            <SectionTitle
              step={2}
              title="Metric profiles"
              subtitle="Upload (or paste) the metric profiles as JSON: source extract queries, metrics, and expected report sections."
            />
            <Stack spacing={2}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={<UploadFileRoundedIcon />}
                >
                  Upload JSON
                  <input
                    type="file"
                    accept="application/json,.json"
                    hidden
                    onChange={onFile}
                  />
                </Button>
                {profilesFileName && (
                  <Chip
                    label={profilesFileName}
                    size="small"
                    onDelete={() => {
                      setProfilesJson("");
                      setProfilesFileName(null);
                      setJsonError(null);
                    }}
                  />
                )}
                {parsedCount > 0 && !jsonError && (
                  <Chip
                    label={`${parsedCount} profile(s) parsed`}
                    color="success"
                    variant="outlined"
                    size="small"
                  />
                )}
              </Stack>

              <TextField
                fullWidth
                label="Metric profiles JSON"
                value={profilesJson}
                onChange={(e) => onJsonChange(e.target.value)}
                multiline
                minRows={10}
                placeholder={EXAMPLE_PROFILES_JSON}
                error={!!jsonError}
                helperText={
                  jsonError ??
                  "Paste JSON directly or upload a file above. Accepts an array of profiles, or an object with a 'metric_profiles' array."
                }
                sx={{
                  "& textarea": {
                    fontFamily: "monospace",
                    fontSize: "0.85rem",
                  },
                }}
              />
            </Stack>
          </CardContent>
        </Card>

        {/* Schedule & recipients */}
        <Card>
          <CardContent>
            <SectionTitle
              step={3}
              title="Schedule & recipients"
              subtitle="When the WBR runs and who receives the published report."
            />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={5}>
                <TextField
                  fullWidth
                  required
                  label="Schedule (cron)"
                  value={schedule}
                  onChange={(e) => setSchedule(e.target.value)}
                  placeholder="0 6 * * MON"
                  helperText="Weekly cadence"
                />
              </Grid>
              <Grid item xs={12} sm={7}>
                <TextField
                  fullWidth
                  required
                  label="Recipients (one per line)"
                  value={recipients}
                  onChange={(e) => setRecipients(e.target.value)}
                  multiline
                  minRows={3}
                  placeholder={"alias@amazon.com\nteam@amazon.com"}
                  helperText={`${recipientCount} recipient(s) · 1-100 allowed`}
                  error={recipientCount > 100}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* WBR skills (optional) */}
        <Card>
          <CardContent>
            <SectionTitle
              step={4}
              title="WBR skills"
              subtitle="Optional. Upload skill files (.md) — reusable instructions the agent applies during report generation (editable later)."
            />
            <Stack spacing={2}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={<UploadFileRoundedIcon />}
                >
                  Upload skill (.md)
                  <input
                    type="file"
                    accept=".md,text/markdown,text/plain"
                    hidden
                    multiple
                    onChange={onSkillFiles}
                  />
                </Button>
                {skills.length > 0 && (
                  <Chip
                    label={`${skills.length} skill(s)`}
                    color="success"
                    variant="outlined"
                    size="small"
                  />
                )}
              </Stack>

              {skillError && <Alert severity="error">{skillError}</Alert>}

              {skills.length > 0 && (
                <List dense disablePadding>
                  {skills.map((s) => (
                    <ListItem
                      key={s.name}
                      sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 2,
                        mb: 1,
                      }}
                      secondaryAction={
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => removeSkill(s.name)}
                        >
                          <DeleteOutlineRoundedIcon fontSize="small" />
                        </IconButton>
                      }
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <DescriptionRoundedIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={s.name}
                        secondary={`${s.body.length.toLocaleString()} characters`}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Stack>
          </CardContent>
        </Card>

        <Divider />

        <Stack
          direction="row"
          justifyContent="flex-end"
          alignItems="center"
          spacing={2}
        >
          <Chip
            label={`${parsedCount} profile(s)`}
            variant="outlined"
            size="small"
          />
          <Button
            variant="contained"
            size="large"
            startIcon={<RocketLaunchRoundedIcon />}
            onClick={submit}
            disabled={!canSubmit}
          >
            {submitting ? "Registering…" : "Register WBR"}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default OnboardWbr;
